import React, { memo, useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { otaVehiclesApi } from "../../api";
import { OTAVehicle, DeploymentLogs } from "../../types";

// ── Layout ────────────────────────────────────────────────────────────────────

const Root = styled.div`
  display: flex;
  gap: 0;
  height: calc(100vh - 160px);
  min-height: 500px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
`;

// ── Left panel — vehicle list ─────────────────────────────────────────────────

const VehiclePanel = styled.div`
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const PanelHeader = styled.div`
  padding: 14px 16px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const VehicleSearch = styled.input`
  margin: 10px 12px;
  padding: 6px 10px;
  font-size: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  outline: none;
  background: #fff;
  color: #172a3f;
  &:focus {
    border-color: #55b8b3;
  }
`;

const VehicleList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const VehicleRow = styled.div<{ $selected: boolean }>`
  padding: 10px 14px;
  cursor: pointer;
  border-left: 3px solid
    ${({ $selected }) => ($selected ? "#55b8b3" : "transparent")};
  background: ${({ $selected }) => ($selected ? "#e6f7f6" : "transparent")};
  transition: background 0.12s;
  &:hover {
    background: ${({ $selected }) => ($selected ? "#e6f7f6" : "#f0fafa")};
  }
`;

const VehicleName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #172a3f;
  font-family: "Courier New", monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VehicleMeta = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const StatusDot = styled.span<{ $status: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
  background: ${({ $status }) => {
    switch ($status) {
      case "online":
        return "#22c55e";
      case "offline":
      case "disconnected":
        return "#ef4444";
      default:
        return "#94a3b8";
    }
  }};
`;

// ── Right panel — log viewer ──────────────────────────────────────────────────

const LogPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
`;

const LogToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
`;

const LogTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #172a3f;
  margin-right: auto;
  font-family: "Courier New", monospace;
`;

const ToolbarLabel = styled.span`
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
`;

const TimeSelect = styled.select`
  font-size: 12px;
  padding: 5px 10px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  color: #172a3f;
  background: #fff;
  cursor: pointer;
`;

const ComponentInput = styled.input`
  font-size: 12px;
  padding: 5px 10px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  color: #172a3f;
  background: #fff;
  min-width: 200px;
  outline: none;
  &:focus {
    border-color: #55b8b3;
  }
`;

const RefreshBtn = styled.button`
  font-size: 12px;
  color: #55b8b3;
  background: transparent;
  border: 1px solid #55b8b3;
  border-radius: 6px;
  padding: 5px 12px;
  cursor: pointer;
  &:hover {
    background: #f0fafa;
  }
`;

const LogContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const Placeholder = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 14px;
  gap: 8px;
`;

const PlaceholderIcon = styled.div`
  font-size: 36px;
  opacity: 0.4;
`;

const LoadingMsg = styled.div`
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  padding: 40px;
`;

const EmptyMsg = styled.div`
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  padding: 40px;
`;

// ── Component log block ───────────────────────────────────────────────────────

const ComponentBlock = styled.div`
  margin-bottom: 14px;
`;

const ComponentHeader = styled.div<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 7px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: ${({ $open }) => ($open ? "6px 6px 0 0" : "6px")};
  user-select: none;
  &:hover {
    background: #f0f7ff;
  }
`;

const ChevronIcon = styled.span<{ $open: boolean }>`
  font-size: 11px;
  color: #94a3b8;
  transition: transform 0.2s;
  transform: ${({ $open }) => ($open ? "rotate(90deg)" : "rotate(0)")};
  display: inline-block;
`;

const ComponentName = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #172a3f;
  font-family: "Courier New", monospace;
`;

const LineCount = styled.span`
  font-size: 11px;
  color: #94a3b8;
  margin-left: auto;
`;

const LogLines = styled.pre`
  margin: 0;
  padding: 12px 14px;
  background: #0f172a;
  color: #e2e8f0;
  font-family: "Courier New", monospace;
  font-size: 11px;
  line-height: 1.6;
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-top: none;
  border-radius: 0 0 6px 6px;
  max-height: 400px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-all;
`;

const LogLineItem = styled.div<{ $level?: string }>`
  color: ${({ $level }) => {
    const lv = ($level ?? "").toUpperCase();
    if (lv === "ERROR" || lv === "CRITICAL") return "#f87171";
    if (lv === "WARNING" || lv === "WARN") return "#fb923c";
    if (lv === "INFO") return "#86efac";
    return "#e2e8f0";
  }};
`;

// ── Types ─────────────────────────────────────────────────────────────────────

interface ComponentLogsProps {
  name: string;
  lines: string[];
}

// ── ComponentLogs ─────────────────────────────────────────────────────────────

const ComponentLogsBlock = memo<ComponentLogsProps>(({ name, lines }) => {
  const [open, setOpen] = useState(true);

  const levelOf = (line: string): string => {
    const first = line.split(" ")[0].toUpperCase();
    return ["ERROR", "CRITICAL", "WARNING", "WARN", "INFO", "DEBUG"].includes(
      first
    )
      ? first
      : "";
  };

  return (
    <ComponentBlock>
      <ComponentHeader $open={open} onClick={() => setOpen((o) => !o)}>
        <ChevronIcon $open={open}>▶</ChevronIcon>
        <ComponentName>{name}</ComponentName>
        <LineCount>
          {lines.length} line{lines.length !== 1 ? "s" : ""}
        </LineCount>
      </ComponentHeader>
      {open && (
        <LogLines>
          {lines.map((line, i) => (
            <LogLineItem key={i} $level={levelOf(line)}>
              {line}
            </LogLineItem>
          ))}
        </LogLines>
      )}
    </ComponentBlock>
  );
});

// ── TIME_OPTIONS ──────────────────────────────────────────────────────────────

const TIME_OPTIONS = [
  { label: "Last 1 hour", hours: 1 },
  { label: "Last 6 hours", hours: 6 },
  { label: "Last 24 hours", hours: 24 },
  { label: "Last 7 days", hours: 168 },
];

// ── LogsView ──────────────────────────────────────────────────────────────────

export const LogsView = memo(() => {
  const [vehicles, setVehicles] = useState<OTAVehicle[]>([]);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState<OTAVehicle | null>(
    null
  );

  const [hours, setHours] = useState(24);
  const [componentFilter, setComponentFilter] = useState("");
  const [logs, setLogs] = useState<DeploymentLogs["logs"] | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);

  // Load vehicle list once
  useEffect(() => {
    otaVehiclesApi
      .getAll()
      .then(setVehicles)
      .catch(() => {
        toast.error("Failed to load vehicles");
      });
  }, []);

  const fetchLogs = useCallback(async () => {
    if (!selectedVehicle) return;
    setLogsLoading(true);
    setLogs(null);
    try {
      const components = componentFilter
        ? componentFilter
            .split(",")
            .map((c) => c.trim())
            .filter(Boolean)
        : undefined;
      const result = await otaVehiclesApi.getLogs(selectedVehicle.id, {
        hours,
        components,
      });
      setLogs(result.logs);
    } catch {
      toast.error(`Failed to load logs for ${selectedVehicle.awsThingName}`);
      setLogs({});
    } finally {
      setLogsLoading(false);
    }
  }, [selectedVehicle, hours, componentFilter]);

  // Fetch logs whenever the selected vehicle or time range changes
  useEffect(() => {
    if (selectedVehicle) {
      fetchLogs();
    }
  }, [selectedVehicle, hours]); // intentionally omits fetchLogs to avoid re-fetching on componentFilter keystrokes

  const handleVehicleSelect = (v: OTAVehicle) => {
    if (v.id === selectedVehicle?.id) return;
    setSelectedVehicle(v);
    setLogs(null);
    setComponentFilter("");
  };

  const filteredVehicles = vehicleSearch
    ? vehicles.filter(
        (v) =>
          v.awsThingName.toLowerCase().includes(vehicleSearch.toLowerCase()) ||
          (v.fleetName ?? "")
            .toLowerCase()
            .includes(vehicleSearch.toLowerCase())
      )
    : vehicles;

  const logEntries = logs ? Object.entries(logs) : [];

  return (
    <Root>
      {/* ── Left: vehicle list ── */}
      <VehiclePanel>
        <PanelHeader>Vehicles ({vehicles.length})</PanelHeader>
        <VehicleSearch
          placeholder="Search VIN or fleet…"
          value={vehicleSearch}
          onChange={(e) => setVehicleSearch(e.target.value)}
        />
        <VehicleList>
          {filteredVehicles.map((v) => (
            <VehicleRow
              key={v.id}
              $selected={v.id === selectedVehicle?.id}
              onClick={() => handleVehicleSelect(v)}
            >
              <VehicleName>{v.awsThingName}</VehicleName>
              <VehicleMeta>
                <StatusDot $status={v.connectionStatus} />
                {v.fleetName ?? "No fleet"}
              </VehicleMeta>
            </VehicleRow>
          ))}
        </VehicleList>
      </VehiclePanel>

      {/* ── Right: log viewer ── */}
      <LogPanel>
        {!selectedVehicle ? (
          <Placeholder>
            <PlaceholderIcon>📋</PlaceholderIcon>
            Select a vehicle to view its CloudWatch logs
          </Placeholder>
        ) : (
          <>
            <LogToolbar>
              <LogTitle>{selectedVehicle.awsThingName}</LogTitle>
              <ToolbarLabel>Time range:</ToolbarLabel>
              <TimeSelect
                value={hours}
                onChange={(e) => setHours(Number(e.target.value))}
              >
                {TIME_OPTIONS.map((o) => (
                  <option key={o.hours} value={o.hours}>
                    {o.label}
                  </option>
                ))}
              </TimeSelect>
              <ToolbarLabel>Components:</ToolbarLabel>
              <ComponentInput
                placeholder="e.g. com.cyngn.Orchestrator"
                value={componentFilter}
                onChange={(e) => setComponentFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchLogs()}
              />
              <RefreshBtn onClick={fetchLogs}>↻ Refresh</RefreshBtn>
            </LogToolbar>

            <LogContent>
              {logsLoading && <LoadingMsg>Loading logs…</LoadingMsg>}
              {!logsLoading && logs !== null && logEntries.length === 0 && (
                <EmptyMsg>
                  No logs found for this vehicle in the selected time range.
                  <br />
                  LogManager may not have uploaded yet, or try a wider time
                  window.
                </EmptyMsg>
              )}
              {!logsLoading &&
                logEntries.map(([name, lines]) => (
                  <ComponentLogsBlock key={name} name={name} lines={lines} />
                ))}
            </LogContent>
          </>
        )}
      </LogPanel>
    </Root>
  );
});
